import * as VueRouter from 'vue-router';

import { isNumeric } from '@/utils/Common';
import BarsLayoutView from '@/views/layouts/BarsLayoutView.vue';
import EmptyLayoutView from '@/views/layouts/EmptyLayoutView.vue';
import { useAuthStore } from '@/stores/useAuthStore';
import { useLoaderStore } from '@/stores/useLoaderStore';
import { useRouterStore } from '@/stores/useRouterStore';
import * as routeNames from '@/constants/routeNames';
import { brandConfig } from '@/brand';

const router = VueRouter.createRouter({
  history: VueRouter.createWebHistory(import.meta.env.VITE_APP_BASE_URL),
  routes: [
    {
      path: '/',
      meta: {
        isPublic: true,
      },
      component: EmptyLayoutView,
      children: [
        {
          name: routeNames.LOGIN,
          path: '/login',
          meta: {
            isPublic: true,
            isSkipBack: true,
            titles: ['Login'],
          },
          component: async () =>
            await import('@/views/pages/LoginPageView.vue'),
        },
        {
          name: routeNames.INITIAL_ERROR,
          path: '/404',
          meta: {
            isPublic: true,
            isSkipBack: true,
            titles: ['Page not found'],
          },
          component: async () =>
            await import('@/views/pages/ErrorPageView.vue'),
        },
      ],
    },
    {
      path: '/',
      component: BarsLayoutView,
      children: [
        {
          name: routeNames.HOME,
          path: '/home',
          meta: { title: 'Home page' },
          component: async () => await import('@/views/pages/HomePageView.vue'),
        },
        {
          name: routeNames.SEARCH_JOB_ID,
          path: '/search/job-id',
          meta: { titles: ['Search', 'Job ID'] },
          component: async () =>
            await import('@/views/layouts/BarsSearchLayoutView.vue'),
        },
        {
          name: routeNames.SEARCH_ADVANCED,
          path: '/search/advanced',
          meta: { titles: ['Search', 'Advanced'] },
          component: async () =>
            await import('@/views/layouts/BarsSearchLayoutView.vue'),
        },
        {
          name: routeNames.SEARCH_POSTCODE,
          path: '/search/postcode',
          meta: { titles: ['Search', 'Postcode'] },
          component: async () =>
            await import('@/views/layouts/BarsSearchLayoutView.vue'),
          beforeEnter(to, from, next) {
            checkPermissions(to, next);
          },
        },
        {
          name: routeNames.SEARCH_SUBURBS,
          path: '/search/suburbs',
          meta: { titles: ['Search', 'Suburbs'] },
          component: async () =>
            await import('@/views/layouts/BarsSearchLayoutView.vue'),
        },
        {
          name: routeNames.SMS_DAILY_FOLLOW_UP,
          path: '/sms/daily',
          meta: { titles: ['SMS', 'Daily follow-up'] },
          component: async () => await import('@/views/pages/SMSDailyView.vue'),
          beforeEnter(to, from, next) {
            checkPermissions(to, next);
          },
        },
        {
          name: routeNames.SMS_HISTORY,
          path: '/sms/history',
          meta: { titles: ['SMS', 'History'] },
          component: async () =>
            await import('@/views/pages/SmsHistoryView.vue'),
          beforeEnter(to, from, next) {
            checkPermissions(to, next);
          },
        },
        {
          name: routeNames.SMS_CHAT,
          path: '/sms/chat',
          meta: { titles: ['SMS', 'Chat'] },
          component: async () => await import('@/views/pages/SMSChatView.vue'),
          beforeEnter(to, from, next) {
            checkPermissions(to, next);
          },
        },
        {
          name: routeNames.AM_PM_JOBS,
          path: '/reports/am-pm-job',
          meta: { titles: ['Reports', 'AM/PM'] },
          component: async () =>
            await import('@/views/reports/DailyReportView.vue'),
          beforeEnter(to, from, next) {
            checkPermissions(to, next);
          },
        },
        {
          name: routeNames.ETA_REPORT,
          path: '/reports/eta',
          meta: { titles: ['Reports', 'ETA'] },
          component: async () =>
            await import('@/views/reports/EtaReportView.vue'),
        },
        {
          name: routeNames.JOBS,
          path: '/dash',
          meta: { titles: ['Tracking', 'Primary'] },
          component: async () =>
            await import('@/views/dashboards/PrimaryTrackingDashboardView.vue'),
        },
        {
          name: routeNames.JOBS_TV,
          path: '/dash-tv',
          meta: { titles: ['Dashboard TV'] },
          component: async () =>
            await import('@/views/dashboards/PrimaryTrackingDashboardView.vue'),
        },

        //

        {
          name: routeNames.BFIX_DASH,
          path: '/dash-bfix',
          meta: { title: 'BFIX Dash' },
          // component: async () => await import('@/views/dashboards/BfixDashboardView.vue'),
          // TODO: WIP
          component: async () => await import('@/views/pages/HomePageView.vue'),
        },
        {
          name: routeNames.AIR_ROAD_JOBS,
          path: '/airroad/jobs',
          meta: { titles: ['AirRoad job booking'] },
          component: async () =>
            await import('@/views/airRoads/JobsAirRoadView.vue'),
          beforeEnter(to, from, next) {
            checkPermissions(to, next);
          },
        },
        {
          name: routeNames.AIR_ROAD_JOBS_NEW,
          path: '/airroad/jobs/new',
          meta: { titles: ['AirRoad job booking', 'New'] },
          component: async () =>
            await import('@/views/airRoads/NewAirRoadView.vue'),
          beforeEnter(to, from, next) {
            checkPermissions(to, next);
          },
          props: (route) => {
            // TODO does it work?
            if (
              typeof route.query.job_pattern === 'string' &&
              Boolean(route.query.job_pattern)
            ) {
              return {
                // 123456-2022-01-01
                // TODO: Maybe make a redirect to job_id+job_date path?
                jobId: route.query.job_pattern.split('-')[0],
                jobDate: route.query.job_pattern.split('-').slice(-3).join('-'),
              };
            } else if (route.query.job_id) {
              return {
                jobId: JSON.parse(route.query.job_id as string),
                jobDate: route.query.job_date,
              };
            }
          },
        },
        {
          name: routeNames.AIR_ROAD_BOOKED,
          path: '/airroad/booked',
          meta: { titles: ['AirRoad booked'] },
          component: async () =>
            await import('@/views/airRoads/BookedAirRoadView.vue'),
          beforeEnter(to, from, next) {
            checkPermissions(to, next);
          },
        },
        {
          name: routeNames.JOB_TICKETS,
          path: '/tickets',
          meta: { titles: ['Job tickets'] },
          component: async () =>
            await import('@/views/dashboards/TicketsDashboardView.vue'),
          beforeEnter(to, from, next) {
            checkPermissions(to, next);
          },
        },
        {
          name: routeNames.JOB_BOOKING,
          path: '/jobbooking',
          meta: { titles: ['Job Booking'] },
          component: async () =>
            await import('@/views/jobBookings/HomeJobBookingView.vue'), // TODO WIP
        },
        {
          name: routeNames.BOOKING_COMPLIANCE,
          path: '/jobbooking/compliance',
          meta: { title: 'Booking compliance' },
          // component: async () => await import('@/views/jobBookings/ComplianceJobBookingView.vue'),
          // TODO: WIP
          component: async () => await import('@/views/pages/HomePageView.vue'),
        },
        {
          name: routeNames.PARCEL_JOB_BOOKING,
          path: '/jobbooking/general',
          meta: { title: 'Parcel / Job booking' },
          // component: async () => await import('@/views/jobBookings/GeneralJobBookingView.vue'),
          // TODO: WIP
          component: async () => await import('@/views/pages/HomePageView.vue'),
        },
        // TODO
        // {
        //   path: '',
        //   component: async () => await import('@/views/layouts/JobBookingLayoutView.vue'),
        //   children: [
        //     {
        //       name: routeNames.NEW_JOB_BOOKING,
        //       path: '/jobbooking/new',
        //       meta: { title: 'New / Job Booking', subtitle: 'Create a job' },
        //       component: async () => await import('@/views/jobBookings/NewJobBookingView.vue'),
        //     },
        //   ],
        // },
        {
          name: routeNames.ADVANCED_JOB_BOOKING,
          path: '/jobbooking/advanced',
          meta: { titles: ['Job Booking', 'Advanced'] },
          component: async () =>
            await import('@/views/jobBookings/AdvancedJobBookingView.vue'),
        },
        {
          name: routeNames.DRAFT_ADVANCED_JOB_BOOKING,
          path: '/jobbooking/advanced/draft/:id',
          meta: { titles: ['Job Booking', 'Advanced', 'Draft'] },
          component: async () =>
            await import('@/views/jobBookings/AdvancedJobBookingView.vue'),
        },
        {
          name: routeNames.PAC_JOB_BOOKING,
          path: '/jobbooking/pac',
          meta: { titles: ['Job Booking'] },
          beforeEnter: () =>
            brandConfig.brand === 'pac' || { name: routeNames.JOB_BOOKING },
          component: async () =>
            await import('@/views/jobBookings/PacJobBookingView.vue'),
        },
        {
          name: routeNames.DRAFT_PAC_JOB_BOOKING,
          path: '/jobbooking/pac/draft/:id',
          meta: { titles: ['Job Booking', 'Draft'] },
          beforeEnter: () =>
            brandConfig.brand === 'pac' || { name: routeNames.JOB_BOOKING },
          component: async () =>
            await import('@/views/jobBookings/PacJobBookingView.vue'),
        },
        {
          name: routeNames.PAC_PALLET_JOB_BOOKING,
          path: '/jobbooking/pac/pallets',
          meta: { titles: ['Job Booking', 'Pallets'] },
          beforeEnter: () =>
            brandConfig.brand === 'pac' || { name: routeNames.JOB_BOOKING },
          component: async () =>
            await import('@/views/jobBookings/PacPalletJobBookingView.vue'),
        },
        {
          name: routeNames.PAC_BOOK_BOXES_JOB_BOOKING,
          path: '/jobbooking/pac/boxes',
          meta: { titles: ['Job Booking', 'Boxes'] },
          beforeEnter: () =>
            brandConfig.brand === 'pac' || { name: routeNames.JOB_BOOKING },
          component: async () =>
            await import('@/views/jobBookings/BookBoxesJobBookingView.vue'),
        },
        {
          name: routeNames.SPREADSHEET_JOB_BOOKING,
          path: '/jobbooking/spreadsheet',
          meta: { titles: ['Spreadsheet', 'Job Booking'] },
          component: async () =>
            await import('@/views/jobBookings/SpreadsheetJobBookingView.vue'),
        },
        {
          name: routeNames.SPREADSHEET_B2B_BOOKING,
          path: '/jobbooking/b2b-spreadsheet',
          meta: { title: 'Spreadsheet / B2B booking' },
          // component: async () => await import('@/views/jobBookings/B2BSpreadsheetJobBookingView.vue'),
          // TODO: WIP
          component: async () => await import('@/views/pages/HomePageView.vue'),
        },
        {
          name: routeNames.DRAFT_JOB_BOOKING,
          path: '/jobbooking/drafts',
          meta: { titles: ['Job Booking', 'Drafts'] },
          component: async () =>
            await import('@/views/jobBookings/DraftsJobBookingView.vue'),
        },
        {
          name: routeNames.WAREHOUSE_QUANT_PAGE,
          path: '/jobbooking/quants',
          meta: { title: 'Warehouse / Quant page' },
          // component: async () => await import('@/views/jobBookings/WarehouseQuantsJobBookingView.vue'),
          // TODO: WIP
          component: async () => await import('@/views/pages/HomePageView.vue'),
        },
        // Job Edit page
        {
          name: routeNames.JOB_EDIT,
          path: '/jobedit',
          meta: { titles: ['Job Edit'] },
          component: async () =>
            await import('@/views/pages/JobEditPageView.vue'),
          props: (route) => {
            if (
              typeof route.query.job_pattern === 'string' &&
              Boolean(route.query.job_pattern)
            ) {
              return {
                jobId: route.query.job_pattern.split('-')[0],
                jobDate: route.query.job_pattern.split('-').slice(-3).join('-'),
              };
            } else if (route.query.job_id) {
              return {
                jobId: Number(route.query.job_id),
                jobDate: route.query.job_date,
              };
            }
          },
        },
        // Detailed job by rh_id
        {
          name: routeNames.DETAILED_PARAM_JOB,
          path: '/job/:job_id',
          meta: { titles: ['Detailed Job', ''] },
          component: async () =>
            await import('@/views/pages/DetailedJobPageView.vue'),
        },
        {
          name: routeNames.DETAILED_QUERY_JOB,
          path: '/detailed-job',
          meta: { titles: ['Detailed Job', ''] },
          component: async () =>
            await import('@/views/pages/DetailedJobPageView.vue'),
          props: (route) => {
            // TODO does it work?
            if (
              typeof route.query.job_pattern === 'string' &&
              Boolean(route.query.job_pattern)
            ) {
              return {
                // 123456-2022-01-01
                // TODO: Maybe make a redirect to job_id+job_date path?
                jobId: route.query.job_pattern.split('-')[0],
                jobDate: route.query.job_pattern.split('-').slice(-3).join('-'),
              };
            } else if (route.query.job_id) {
              return {
                jobId: JSON.parse(route.query.job_id as string),
                jobDate: route.query.job_date,
              };
            }
          },
        },
        {
          name: routeNames.TICKET_FROM_THE_SERVICE_DESK,
          path: '/job/external/:id',
          meta: { title: 'Service desk ticket from' },
          redirect: (to) => {
            const ids = to.path.split('/job/external/')[1];
            // Multiple IDs
            if (ids.includes(',')) {
              // Redirect to the Error page if some word isn't a number
              const hasInvalidNumber = ids.split(',').some((id) => {
                if (!isNumeric(id)) {
                  return true;
                }
                return false;
              });
              if (hasInvalidNumber) {
                return { path: '/error' };
              }
              // Redirect to the Search page
              return { path: '/search', query: { job_id: ids } };
            }
            // Single ID
            return { path: `/job/${ids}` };
          },
        },
        {
          name: routeNames.SERVICE_DESK_TICKET,
          path: '/job/ticket/:ticket_id',
          meta: { titles: ['Job Ticket', ''] },
          component: async () =>
            await import('@/views/pages/ZammadTicketPageView.vue'),
        },
        {
          name: routeNames.LIST_QUOTES,
          path: '/quotes/list',
          meta: { title: 'Quotes' },
          // component: async () => await import('@/views/quotes/ListQuoteView.vue'),
          // TODO: WIP
          component: async () => await import('@/views/pages/HomePageView.vue'),
        },
        {
          name: routeNames.NEW_QUOTE,
          path: '/quotes/new',
          meta: { title: 'New quote' },
          // component: async () => await import('@/views/quotes/NewQuoteView.vue'),
          // TODO: WIP
          component: async () => await import('@/views/pages/HomePageView.vue'),
        },
        {
          name: routeNames.DETAILED_QUOTE,
          path: '/quotes/:id',
          meta: { title: 'Detailed quote' },
          // component: async () => await import('@/views/quotes/DetailedQuoteView.vue'),
          // TODO: WIP
          component: async () => await import('@/views/pages/HomePageView.vue'),
        },
        {
          name: routeNames.DISPATCH,
          path: '/dispatch',
          meta: { titles: ['Dispatch'] },
          component: async () =>
            await import('@/views/dashboards/DispatchDashboardView.vue'),
          beforeEnter(to, from, next) {
            checkPermissions(to, next);
          },
        },
        {
          name: routeNames.SCHEDULER_BOARD,
          path: '/scheduler/board/:division/:date',
          meta: { title: 'Scheduler board' },
          // component: async () => await import('@/views/schedulers/BoardSchedulerView.vue'),
          // TODO: WIP
          component: async () => await import('@/views/pages/HomePageView.vue'),
        },
        {
          name: routeNames.SCHEDULER_BOARD_LOGS,
          path: '/scheduler/board/:division/:date/logs',
          meta: { title: 'Scheduler board logs' },
          // component: async () => await import('@/views/schedulers/BoardLogsSchedulerView.vue'),
          // TODO: WIP
          component: async () => await import('@/views/pages/HomePageView.vue'),
        },
        {
          name: routeNames.SCHEDULER,
          // path: '/scheduler/:date?',
          path: '/scheduler',
          meta: { title: 'Scheduler page' },
          component: async () =>
            await import('@/views/schedulers/HomeSchedulerView.vue'),
        },
        {
          name: routeNames.SCHEDULER_SHIFTS,
          path: '/scheduler/shifts/:date?',
          meta: { title: 'Scheduler Shifts' },
          // component: async () => await import('@/views/schedulers/ShiftsSchedulerView.vue'),
          // TODO: WIP
          component: async () => await import('@/views/pages/HomePageView.vue'),
        },
        {
          name: routeNames.PROFILE,
          path: '/profile',
          meta: { titles: ['Profile', 'Details'] },
          component: async () =>
            await import('@/views/pages/ProfilePageView.vue'),
        },
        {
          name: routeNames.PROFILE_NOTIFICATIONS,
          path: '/profile/notifications',
          meta: { titles: ['Profile', 'Notifications'] },
          component: async () =>
            await import('@/views/pages/ProfilePageView.vue'),
        },
        {
          name: routeNames.PROFILE_SETTINGS,
          path: '/profile/settings',
          meta: { titles: ['Profile', 'Settings'] },
          component: async () =>
            await import('@/views/pages/ProfilePageView.vue'),
        },
        {
          name: routeNames.ADDRESS_BOOK,
          path: '/user/address-book',
          meta: { title: 'Address Book' },
          component: async () =>
            await import('@/views/profiles/UserAddressBookProfileView.vue'),
        },
        {
          name: routeNames.REPORTS,
          path: '/reports/reports',
          meta: { title: 'Reports' },
          // component: async () => await import('@/views/reports/BulkReportView.vue'),
          // TODO: WIP
          component: async () => await import('@/views/pages/HomePageView.vue'),
          beforeEnter(to, from, next) {
            checkPermissions(to, next);
          },
        },
        {
          name: routeNames.RUN_SHEET,
          path: '/reports/runsheet',
          meta: { titles: ['Reports', 'Run Sheet'] },
          component: async () =>
            await import('@/views/reports/RunSheetReportView.vue'),
          beforeEnter(to, from, next) {
            checkPermissions(to, next);
          },
        },
        {
          name: routeNames.KPI_REPORT,
          path: '/reports/kpi',
          meta: { titles: ['Reports', 'KPI'] },
          component: async () =>
            await import('@/views/reports/KpiReportView.vue'),
          beforeEnter(to, from, next) {
            checkPermissions(to, next);
          },
        },
        {
          name: routeNames.DRIVER_REPORT,
          path: '/reports/driver',
          meta: { titles: ['Reports', 'Driver'] },
          component: async () =>
            await import('@/views/reports/DriverReportView.vue'),
          beforeEnter(to, from, next) {
            checkPermissions(to, next);
          },
        },
        {
          name: routeNames.TOMORROW_REPORT,
          path: '/reports/tomorrow',
          meta: { titles: ['Reports', 'Tomorrow'] },
          component: async () =>
            await import('@/views/reports/TomorrowReportView.vue'),
          beforeEnter(to, from, next) {
            checkPermissions(to, next);
          },
        },
        {
          name: routeNames.WEEKLY_STATS_REPORT,
          path: '/reports/weekly-stats',
          meta: { titles: ['Reports', 'Weekly'] },
          component: async () =>
            await import('@/views/reports/WeeklyReportView.vue'),
          beforeEnter(to, from, next) {
            checkPermissions(to, next);
          },
        },
        {
          name: routeNames.BOOKED_JOBS,
          path: '/jobbooking/booked',
          meta: { titles: ['Job Booking', 'Booked Jobs'] },
          component: async () =>
            await import('@/views/jobBookings/BookedJobBookingView.vue'),
        },
        {
          name: routeNames.JOB_HISTORY,
          path: '/job-history',
          meta: { titles: ['Job History', ''] },
          component: async () =>
            await import('@/views/pages/JobHistoryPageView.vue'),
          beforeEnter(to, from, next) {
            checkPermissions(to, next);
          },
        },
        {
          path: '/old-version',
          name: routeNames.OLD_VERSION,
          component: {
            beforeRouteEnter() {
              window.location.href = import.meta.env.VITE_OLD_VERSION_URL;
            },
            render: () => null,
          },
        },
      ],
    },
    {
      name: routeNames.DEFAULT,
      path: '/',
      strict: true,
      redirect: { name: routeNames.HOME },
    },
    {
      name: routeNames.SEARCH,
      path: '/search',
      strict: true,
      redirect: { name: routeNames.SEARCH_JOB_ID },
    },
    {
      name: routeNames.PAGE_ERROR,
      path: '/:pathMatch(.*)*',
      redirect: { name: routeNames.INITIAL_ERROR },
    },
  ],
});

const notStaffRoutes = [
  routeNames.BFIX_DASH,
  routeNames.AIR_ROAD_JOBS,
  routeNames.AIR_ROAD_BOOKED,
  routeNames.BOOKING_COMPLIANCE,
  routeNames.SCHEDULER_BOARD,
  routeNames.SCHEDULER_BOARD_LOGS,
  routeNames.SCHEDULER,
  routeNames.SCHEDULER_SHIFTS,
  routeNames.REPORTS,
  routeNames.RUN_SHEET,
  routeNames.KPI_REPORT,
  routeNames.DRIVER_REPORT,
  routeNames.TOMORROW_REPORT,
  routeNames.WEEKLY_STATS_REPORT,
  routeNames.AM_PM_JOBS,
  routeNames.LOCATIONS,
  routeNames.JOB_HISTORY,
  routeNames.SEARCH_POSTCODE,
  routeNames.DISPATCH,
  routeNames.SMS_DAILY_FOLLOW_UP,
  routeNames.SMS_HISTORY,
  routeNames.SMS_CHAT,
];

const checkPermissions = (
  to: VueRouter.RouteLocationNormalized,
  next: VueRouter.NavigationGuardNext,
): void => {
  const authStore = useAuthStore();
  const permissions = authStore.authData.permissions;
  let stuff = false;
  if (Array.isArray(permissions)) {
    permissions.forEach((permission): void => {
      // looking for Job/staff
      if (
        permission.subject === 'Job' &&
        permission.actions.includes('staff')
      ) {
        stuff = true;
      }
    });
  }
  // redirecting user if the route is the list & user is not staff
  if (
    // TODO
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    !stuff &&
    typeof to.name === 'string' &&
    notStaffRoutes.includes(to.name)
  ) {
    next({
      path: '/',
    });
  }
  next();
};

/**
 * Before each page we check auth status from store and for private pages.
 */
router.beforeEach((to, from, next) => {
  const routerStore = useRouterStore();
  const authStore = useAuthStore();
  const loaderStore = useLoaderStore();

  if (process.env.NODE_ENV === 'development') {
    console.log({ from, to });
  }

  // don't show global loader when only query is changed
  if (to.name !== from.name) {
    loaderStore.setGlobalLoader(true); // TODO when fresh login it runs more times then `loaderStore.setGlobalLoader(false)`
  }

  if (to.matched.some((record) => !record.meta.isPublic)) {
    if (!authStore.isLoggedIn && to.name !== routeNames.LOGIN) {
      next({ path: '/login' });
    } else {
      // If we are going anywhere from Login page where we have been push from previous page
      // we redirect to that previous page
      if (routerStore.prevUrl.url !== '' && from.name === routeNames.LOGIN) {
        const prevPath = routerStore.prevUrl.url;
        routerStore.setPrevUrl({
          name: '',
          url: '',
        });
        next({ path: prevPath });
        // Don't know what is it for?
      } else if (
        (routerStore.prevUrl.name === routeNames.DETAILED_QUERY_JOB &&
          from.name !== routeNames.DETAILED_QUERY_JOB) ||
        (routerStore.prevUrl.name === routeNames.DETAILED_PARAM_JOB &&
          from.name !== routeNames.DETAILED_PARAM_JOB)
      ) {
        routerStore.setPrevUrl({
          name: '',
          url: '',
        });
        next();
      } else {
        next();
      }
    }
  } else {
    // Write down "TO" route in Store to be able to redirect there after successful LogIn
    // in the case when use has been pushed to "login" from somewhere
    if (
      typeof from.name === 'string' &&
      to.name === routeNames.LOGIN &&
      to.name !== from.name
    ) {
      routerStore.setPrevUrl({
        name: from.name,
        url: from.fullPath,
      });
    }
    next();
  }
});

router.beforeResolve((to, from, next) => {
  const loaderStore = useLoaderStore();

  loaderStore.setGlobalLoader(false); // TODO when fresh login it runs less times then `loaderStore.setGlobalLoader(true)`
  next();
});

router.afterEach((to, from) => {
  const routerStore = useRouterStore();
  if (routerStore.isShiftBackRoute) {
    routerStore.shiftBackRoute();
  } else if (!from.meta.isSkipBack) {
    if (to.name !== from.name) {
      routerStore.addBackRoute({
        name: from.name,
        query: { ...from.query },
        params: { ...from.params },
      });
    }
  }
});

router.afterEach((to, from) => {
  const mainTitle = brandConfig.portalTitle;
  const titles = to.meta.titles as string[] | undefined;
  if (titles?.length) {
    // toReversed() doesn't comply our browserlist
    const title = titles
      .slice()
      .reverse()
      .filter((_title) => _title.trim().length);
    document.title = title.join(' / ') + ' / ' + mainTitle;
  } else {
    document.title = mainTitle;
  }
});

// TODO
// router.afterEach((to, from) => {
//   Vue.nextTick(() => {
//     const title = to.meta?.title || '';
//     document.title = 'KK ' + title;
//   });
// });

export default router;
